export const CANONICAL_TREE_RETIREMENT_READINESS_INSTALLATION_IDENTITY_STORAGE_KEY =
  'kc:tree-retirement-readiness-installation-identity:v1' as const;

export const CANONICAL_TREE_RETIREMENT_READINESS_INSTALLATION_IDENTITY_SCHEMA_VERSION =
  1 as const;

export const CANONICAL_TREE_RETIREMENT_READINESS_INSTALLATION_IDENTITY_EVENT =
  'kc:tree-retirement-readiness-installation-identity:changed' as const;

export type CanonicalTreeRetirementReadinessInstallationIdentityQuarantineReason =
  | 'INVALID_JSON'
  | 'INVALID_ENVELOPE'
  | 'INVALID_UUID'
  | 'UNSUPPORTED_SCHEMA'
  | 'STORAGE_UNAVAILABLE';

export type CanonicalTreeRetirementReadinessInstallationIdentityPersistence =
  | 'PERSISTED'
  | 'MEMORY_ONLY'
  | 'UNAVAILABLE';

export interface CanonicalTreeRetirementReadinessInstallationIdentityQuarantine {
  readonly detectedAt:
    string;

  readonly reason:
    CanonicalTreeRetirementReadinessInstallationIdentityQuarantineReason;

  readonly sourceKey:
    typeof CANONICAL_TREE_RETIREMENT_READINESS_INSTALLATION_IDENTITY_STORAGE_KEY;

  readonly rawPayloadPersisted:
    false;
}

export interface CanonicalTreeRetirementReadinessInstallationIdentityEnvelope {
  readonly schemaVersion:
    typeof CANONICAL_TREE_RETIREMENT_READINESS_INSTALLATION_IDENTITY_SCHEMA_VERSION;

  readonly installationId:
    string;

  readonly createdAt:
    string;

  readonly updatedAt:
    string;

  readonly quarantine:
    CanonicalTreeRetirementReadinessInstallationIdentityQuarantine | null;
}

export interface CanonicalTreeRetirementReadinessInstallationIdentitySnapshot {
  readonly generatedAt:
    string;

  readonly installationId:
    string | null;

  readonly persistence:
    CanonicalTreeRetirementReadinessInstallationIdentityPersistence;

  readonly envelope:
    CanonicalTreeRetirementReadinessInstallationIdentityEnvelope | null;

  readonly quarantine:
    CanonicalTreeRetirementReadinessInstallationIdentityQuarantine | null;

  readonly browserProfileScoped:
    true;

  readonly accountScoped:
    false;

  readonly containsPrivateFamilyData:
    false;

  readonly containsAuthenticationData:
    false;

  readonly fleetCoverageCertified:
    false;

  readonly evidencePublished:
    false;

  readonly supabaseWritePerformed:
    false;

  readonly retirementAuthorized:
    false;
}

type IdentitySubscriber =
  (
    snapshot:
      CanonicalTreeRetirementReadinessInstallationIdentitySnapshot,
  ) => void;

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

let memoryEnvelope:
  CanonicalTreeRetirementReadinessInstallationIdentityEnvelope | null =
    null;

let memoryPersistence:
  CanonicalTreeRetirementReadinessInstallationIdentityPersistence =
    'UNAVAILABLE';

let loaded =
  false;

const subscribers =
  new Set<IdentitySubscriber>();

const validDate =
  (
    value:
      unknown,
  ): value is string =>
    typeof value === 'string' &&
    !Number.isNaN(
      Date.parse(value),
    );

const isRecord =
  (
    value:
      unknown,
  ): value is Readonly<Record<string, unknown>> =>
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value);

export const isCanonicalTreeRetirementReadinessInstallationUuid =
  (
    value:
      unknown,
  ): value is string =>
    typeof value === 'string' &&
    UUID_PATTERN.test(value);

const isQuarantine =
  (
    value:
      unknown,
  ): value is CanonicalTreeRetirementReadinessInstallationIdentityQuarantine | null => {
    if (value === null) {
      return true;
    }

    if (!isRecord(value)) {
      return false;
    }

    return (
      validDate(value.detectedAt) &&
      (
        value.reason === 'INVALID_JSON' ||
        value.reason === 'INVALID_ENVELOPE' ||
        value.reason === 'INVALID_UUID' ||
        value.reason === 'UNSUPPORTED_SCHEMA' ||
        value.reason === 'STORAGE_UNAVAILABLE'
      ) &&
      value.sourceKey ===
        CANONICAL_TREE_RETIREMENT_READINESS_INSTALLATION_IDENTITY_STORAGE_KEY &&
      value.rawPayloadPersisted ===
        false
    );
  };

const parseEnvelope =
  (
    value:
      unknown,
  ): CanonicalTreeRetirementReadinessInstallationIdentityEnvelope | null => {
    if (!isRecord(value)) {
      return null;
    }

    if (
      value.schemaVersion !==
        CANONICAL_TREE_RETIREMENT_READINESS_INSTALLATION_IDENTITY_SCHEMA_VERSION ||
      !isCanonicalTreeRetirementReadinessInstallationUuid(
        value.installationId,
      ) ||
      !validDate(
        value.createdAt,
      ) ||
      !validDate(
        value.updatedAt,
      ) ||
      !isQuarantine(
        value.quarantine,
      )
    ) {
      return null;
    }

    return Object.freeze({
      schemaVersion:
        CANONICAL_TREE_RETIREMENT_READINESS_INSTALLATION_IDENTITY_SCHEMA_VERSION,

      installationId:
        value.installationId,

      createdAt:
        value.createdAt,

      updatedAt:
        value.updatedAt,

      quarantine:
        value.quarantine,
    });
  };

const createQuarantine =
  (
    reason:
      CanonicalTreeRetirementReadinessInstallationIdentityQuarantineReason,
  ): CanonicalTreeRetirementReadinessInstallationIdentityQuarantine =>
    Object.freeze({
      detectedAt:
        new Date().toISOString(),

      reason,

      sourceKey:
        CANONICAL_TREE_RETIREMENT_READINESS_INSTALLATION_IDENTITY_STORAGE_KEY,

      rawPayloadPersisted:
        false,
    });

const generateInstallationUuid =
  (): string | null => {
    const cryptoAuthority =
      globalThis.crypto;

    if (
      !cryptoAuthority ||
      typeof cryptoAuthority.randomUUID !==
        'function'
    ) {
      return null;
    }

    const candidate =
      cryptoAuthority.randomUUID();

    return isCanonicalTreeRetirementReadinessInstallationUuid(
      candidate,
    )
      ? candidate
      : null;
  };

const createEnvelope =
  (
    quarantine:
      CanonicalTreeRetirementReadinessInstallationIdentityQuarantine | null,
  ): CanonicalTreeRetirementReadinessInstallationIdentityEnvelope | null => {
    const installationId =
      generateInstallationUuid();

    if (!installationId) {
      return null;
    }

    const now =
      new Date().toISOString();

    return Object.freeze({
      schemaVersion:
        CANONICAL_TREE_RETIREMENT_READINESS_INSTALLATION_IDENTITY_SCHEMA_VERSION,

      installationId,

      createdAt:
        now,

      updatedAt:
        now,

      quarantine,
    });
  };

const createSnapshot =
  (): CanonicalTreeRetirementReadinessInstallationIdentitySnapshot =>
    Object.freeze({
      generatedAt:
        new Date().toISOString(),

      installationId:
        memoryEnvelope?.installationId ??
        null,

      persistence:
        memoryPersistence,

      envelope:
        memoryEnvelope,

      quarantine:
        memoryEnvelope?.quarantine ??
        null,

      browserProfileScoped:
        true,

      accountScoped:
        false,

      containsPrivateFamilyData:
        false,

      containsAuthenticationData:
        false,

      fleetCoverageCertified:
        false,

      evidencePublished:
        false,

      supabaseWritePerformed:
        false,

      retirementAuthorized:
        false,
    });

const notifySubscribers =
  (): void => {
    const snapshot =
      createSnapshot();

    for (const subscriber of subscribers) {
      try {
        subscriber(
          snapshot,
        );
      } catch {
        // Subscriber failures must not alter installation identity state.
      }
    }

    if (
      typeof window !==
      'undefined'
    ) {
      try {
        window.dispatchEvent(
          new CustomEvent(
            CANONICAL_TREE_RETIREMENT_READINESS_INSTALLATION_IDENTITY_EVENT,
            {
              detail:
                snapshot,
            },
          ),
        );
      } catch {
        // Same-document notification is best effort.
      }
    }
  };

const readPersistedEnvelope =
  (): CanonicalTreeRetirementReadinessInstallationIdentityEnvelope | null => {
    if (
      typeof window ===
      'undefined'
    ) {
      return null;
    }

    const raw =
      window.localStorage.getItem(
        CANONICAL_TREE_RETIREMENT_READINESS_INSTALLATION_IDENTITY_STORAGE_KEY,
      );

    if (!raw) {
      return null;
    }

    let parsed:
      unknown;

    try {
      parsed =
        JSON.parse(
          raw,
        );
    } catch {
      return null;
    }

    return parseEnvelope(
      parsed,
    );
  };

const persistEnvelopeAndReread =
  (
    envelope:
      CanonicalTreeRetirementReadinessInstallationIdentityEnvelope,
  ): CanonicalTreeRetirementReadinessInstallationIdentityEnvelope | null => {
    if (
      typeof window ===
      'undefined'
    ) {
      return null;
    }

    window.localStorage.setItem(
      CANONICAL_TREE_RETIREMENT_READINESS_INSTALLATION_IDENTITY_STORAGE_KEY,
      JSON.stringify(
        envelope,
      ),
    );

    return readPersistedEnvelope();
  };

const replaceIdentity =
  (
    reason:
      CanonicalTreeRetirementReadinessInstallationIdentityQuarantineReason | null,
  ): CanonicalTreeRetirementReadinessInstallationIdentitySnapshot => {
    const quarantine =
      reason
        ? createQuarantine(
            reason,
          )
        : null;

    const candidate =
      createEnvelope(
        quarantine,
      );

    if (!candidate) {
      memoryEnvelope =
        null;

      memoryPersistence =
        'UNAVAILABLE';

      loaded =
        true;

      notifySubscribers();

      return createSnapshot();
    }

    if (
      typeof window ===
      'undefined'
    ) {
      memoryEnvelope =
        candidate;

      memoryPersistence =
        'MEMORY_ONLY';

      loaded =
        true;

      notifySubscribers();

      return createSnapshot();
    }

    try {
      const persisted =
        persistEnvelopeAndReread(
          candidate,
        );

      if (!persisted) {
        memoryEnvelope =
          candidate;

        memoryPersistence =
          'MEMORY_ONLY';
      } else {
        memoryEnvelope =
          persisted;

        memoryPersistence =
          'PERSISTED';
      }
    } catch {
      const storageQuarantine =
        createQuarantine(
          'STORAGE_UNAVAILABLE',
        );

      const memoryCandidate =
        createEnvelope(
          storageQuarantine,
        );

      memoryEnvelope =
        memoryCandidate;

      memoryPersistence =
        memoryCandidate
          ? 'MEMORY_ONLY'
          : 'UNAVAILABLE';
    }

    loaded =
      true;

    notifySubscribers();

    return createSnapshot();
  };

const classifyInvalidStoredValue =
  (
    raw:
      string,
  ): CanonicalTreeRetirementReadinessInstallationIdentityQuarantineReason => {
    let parsed:
      unknown;

    try {
      parsed =
        JSON.parse(
          raw,
        );
    } catch {
      return 'INVALID_JSON';
    }

    if (
      isRecord(parsed) &&
      parsed.schemaVersion !==
        CANONICAL_TREE_RETIREMENT_READINESS_INSTALLATION_IDENTITY_SCHEMA_VERSION
    ) {
      return 'UNSUPPORTED_SCHEMA';
    }

    if (
      isRecord(parsed) &&
      !isCanonicalTreeRetirementReadinessInstallationUuid(
        parsed.installationId,
      )
    ) {
      return 'INVALID_UUID';
    }

    return 'INVALID_ENVELOPE';
  };

const loadIdentity =
  (
    forceReload =
      false,
  ): CanonicalTreeRetirementReadinessInstallationIdentitySnapshot => {
    if (
      loaded &&
      !forceReload
    ) {
      return createSnapshot();
    }

    if (
      typeof window ===
      'undefined'
    ) {
      return replaceIdentity(
        null,
      );
    }

    let raw:
      string | null;

    try {
      raw =
        window.localStorage.getItem(
          CANONICAL_TREE_RETIREMENT_READINESS_INSTALLATION_IDENTITY_STORAGE_KEY,
        );
    } catch {
      return replaceIdentity(
        'STORAGE_UNAVAILABLE',
      );
    }

    if (!raw) {
      return replaceIdentity(
        null,
      );
    }

    let parsed:
      unknown;

    try {
      parsed =
        JSON.parse(
          raw,
        );
    } catch {
      return replaceIdentity(
        'INVALID_JSON',
      );
    }

    const envelope =
      parseEnvelope(
        parsed,
      );

    if (!envelope) {
      return replaceIdentity(
        classifyInvalidStoredValue(
          raw,
        ),
      );
    }

    memoryEnvelope =
      envelope;

    memoryPersistence =
      'PERSISTED';

    loaded =
      true;

    return createSnapshot();
  };

export const readCanonicalTreeRetirementReadinessInstallationIdentity =
  (): CanonicalTreeRetirementReadinessInstallationIdentitySnapshot =>
    loadIdentity();

export const reloadCanonicalTreeRetirementReadinessInstallationIdentityFromStorage =
  (): CanonicalTreeRetirementReadinessInstallationIdentitySnapshot =>
    loadIdentity(
      true,
    );

export const subscribeCanonicalTreeRetirementReadinessInstallationIdentity =
  (
    subscriber:
      IdentitySubscriber,
  ): (() => void) => {
    subscribers.add(
      subscriber,
    );

    const storageHandler =
      (
        event:
          StorageEvent,
      ): void => {
        if (
          event.key !==
          CANONICAL_TREE_RETIREMENT_READINESS_INSTALLATION_IDENTITY_STORAGE_KEY
        ) {
          return;
        }

        loadIdentity(
          true,
        );

        subscriber(
          createSnapshot(),
        );
      };

    if (
      typeof window !==
      'undefined'
    ) {
      window.addEventListener(
        'storage',
        storageHandler,
      );
    }

    subscriber(
      loadIdentity(),
    );

    return (): void => {
      subscribers.delete(
        subscriber,
      );

      if (
        typeof window !==
        'undefined'
      ) {
        window.removeEventListener(
          'storage',
          storageHandler,
        );
      }
    };
  };

export const clearCanonicalTreeRetirementReadinessInstallationIdentityForTest =
  (): void => {
    memoryEnvelope =
      null;

    memoryPersistence =
      'UNAVAILABLE';

    loaded =
      false;

    if (
      typeof window !==
      'undefined'
    ) {
      try {
        window.localStorage.removeItem(
          CANONICAL_TREE_RETIREMENT_READINESS_INSTALLATION_IDENTITY_STORAGE_KEY,
        );
      } catch {
        // Test reset remains best effort when storage is unavailable.
      }
    }
  };

export const describeCanonicalTreeRetirementReadinessInstallationIdentityImplementation =
  () =>
    Object.freeze({
      milestone:
        'CC-TREE-ARCH-092',

      authority:
        'TREE_HISTORICAL_AUTHORITY_PRESENTATION_OBSERVABILITY_LIFECYCLE_RETIREMENT_READINESS_PSEUDONYMOUS_INSTALLATION_IDENTITY_IMPLEMENTATION',

      decision:
        'TREE_HISTORICAL_AUTHORITY_PRESENTATION_OBSERVABILITY_LIFECYCLE_RETIREMENT_READINESS_PSEUDONYMOUS_INSTALLATION_IDENTITY_IMPLEMENTED',

      storageKey:
        CANONICAL_TREE_RETIREMENT_READINESS_INSTALLATION_IDENTITY_STORAGE_KEY,

      schemaVersion:
        CANONICAL_TREE_RETIREMENT_READINESS_INSTALLATION_IDENTITY_SCHEMA_VERSION,

      browserProfileScoped:
        true,

      accountScoped:
        false,

      cryptographicUuidOnly:
        true,

      insecureFallbackAllowed:
        false,

      storageEventSynchronization:
        true,

      sameDocumentSynchronization:
        true,

      simultaneousFirstRunPolicy:
        'LAST_VALID_PERSISTED_ENVELOPE_WINS',

      rawMalformedPayloadPersisted:
        false,

      browserWriterCreated:
        true,

      installationIdentifierGeneratedInApplication:
        true,

      identityModuleImportsSupabase:
        false,

      identityModuleInvokesRpc:
        false,

      evidencePublished:
        false,

      readinessProjectionRead:
        false,

      supabaseWritePerformed:
        false,

      treeWorkspaceWired:
        false,

      commandCenterWired:
        false,

      runtimeDecisionDependencyAdded:
        false,

      runtimeGuardDependencyAdded:
        false,

      retirementAuthorized:
        false,

      certifiesTreeHealth:
        false,

      certifiesReleaseReadiness:
        false,

      certifiesDeploymentReadiness:
        false,

      nextGovernedDependency:
        'TREE_HISTORICAL_AUTHORITY_PRESENTATION_OBSERVABILITY_LIFECYCLE_RETIREMENT_READINESS_PSEUDONYMOUS_INSTALLATION_IDENTITY_CERTIFICATION',
    } as const);
